import React, { useState, useEffect, useCallback } from 'react';
import { MoneyWithCoverage } from './PipelineBoard';
import {
  Activity, AlertTriangle, TrendingUp, Sparkles, RefreshCw, X,
  Send, Brain, Target, Calendar, DollarSign, ChevronRight, CheckCircle2, Clock,
} from 'lucide-react';
import { canView, canUse } from '../../lib/aiPermissions';

/* ──────────────────────────────────────────────────────────────────────────
 * Phase G — Pipeline Intelligence components
 *
 * Six features bundled into one file (each is small enough not to warrant
 * its own file, and they share styling + utilities):
 *
 *   PipelineHealthStrip   — G.1 — KPI strip + at-risk count (no AI)
 *   ForecastWidget        — G.4 — predictive forecast w/ confidence interval
 *   AdvanceSuggestionsBanner — G.3 — Haiku bulk: which contacts ready to advance
 *   CopilotChat           — G.6 — Opus: natural-language pipeline queries
 *   DealHealthChip        — G.2 — Haiku per-contact health score chip
 *   NextBestActionChip    — G.5 — Haiku per-contact NBA recommendation
 *
 * Permission-gated via aiPermissions canView/canUse. Each renders silently
 * (returns null) when the role lacks access.
 * ────────────────────────────────────────────────────────────────────────── */

// useResolvedRole() removed Task #203 — role is now passed in as a prop
// from CRMPage (which has user.role from its own user prop). Killed 3-4
// getOwnProfile round trips per CRM page bootstrap; meaningful for users
// far from us-east-1 (San Diego, Cancun, Tijuana, Puerto Vallarta).

const cardStyle = {
  background: '#FFFFFF', border: '1px solid #E2E8F0',
  borderRadius: 10, padding: 12,
};

/** Share of a denominator, or an em-dash when there is no denominator to divide by. */
const pct = (n, d) => (d ? `${((Number(n || 0) / Number(d)) * 100).toFixed(1)}%` : '—');

/* ─── G.1 + G.4 — KPI strip + Forecast (combined into one row) ─────────── */

/**
 * ⛔⛔ THE STRIP IS TOLD WHICH PIPELINE IT IS DESCRIBING — Card P235.
 *
 * It used to take only `role` and call getPipelineHealthMetrics({}) — an
 * empty filter — while the pill beside it showed a pipeline-scoped count.
 * Carlos read "256 open opportunities" directly above a tile reading 8,467.
 * Both were correct; they answered different questions, and the screen said
 * nothing about it.
 */
export function PipelineHealthStrip({ role, pipelineId = null, pipelineName = null,
                                      ownerId = null, from = null, to = null }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reasonsOpen, setReasonsOpen] = useState(false);

  const showHealth = role && canView(role, 'pipeline_health_metrics');

  useEffect(() => {
    if (!showHealth) { setLoading(false); return undefined; }
    let cancelled = false;
    /*
     * ⛔⛔ NO STALE NUMBERS BETWEEN PIPELINES — Card P235 requirement 4.
     * Showing Eden Vitality's figures under Second Pipeline's heading is a
     * wrong answer that looks like a right one, and it is exactly what a
     * screenshot would capture.
     *
     * ⚠⚠ THE OPERATIVE GUARD IS `loading`, NOT THE CLEAR — measured. The
     * component returns the loading card whenever `loading` is true, so
     * removing `setMetrics(null)` changes nothing on screen: I planted that
     * removal and the suite stayed GREEN. The clear is kept as defence for a
     * future refactor that renders metrics beside a spinner, but it is
     * redundant today and no test can distinguish it — said here rather than
     * left as an assertion that cannot fail.
     */
    setMetrics(null);
    setLoading(true);
    (async () => {
      try {
        const h = await window.zignagenixAPI
          .getPipelineHealthMetrics({ pipelineId, ownerId, from, to })
          .catch(() => null);
        if (cancelled) return;
        if (h && !h.error) setMetrics(h);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    /*
     * ⚠ EVERY FILTER IS A DEPENDENCY. The array was [showHealth] alone, so
     * the strip fetched once and never again — switching pipelines would not
     * have updated it even after it learned to accept one.
     */
  }, [showHealth, pipelineId, ownerId, from, to]);

  /*
   * ⭐ THE CAPTION COMES FROM THE SERVER'S ECHO, NOT FROM OUR PROPS — Card
   * P235 requirement 3. A caption built from what we ASKED for would still
   * read "Eden Vitality" if the server had ignored the argument, which is
   * the defect this card exists to fix.
   */
  const sc = metrics && metrics.scope;
  const scopeLabel = !sc ? null : [
    sc.allPipelines ? 'All pipelines' : (sc.pipelineName || pipelineName || `Pipeline ${sc.pipelineId}`),
    sc.ownerId == null ? 'all owners' : 'one owner',
    (sc.from || sc.to) ? 'filtered dates' : 'all dates',
  ].join(' · ');

  if (!showHealth) return null;
  /* ⚠ A LOADING STATE, not stale numbers — requirement 4. */
  if (loading) {
    return (
      <div data-testid="health-strip-loading"
           style={{ ...cardStyle, marginBottom: 12, fontSize: 11, color: '#94A3B8' }}>
        Loading pipeline intelligence…
      </div>
    );
  }

  return (
    <>
      {/* ⭐ One caption for the strip, from the server's echo — so a
          screenshot cannot be misread. Requirement 3. */}
      {scopeLabel ? (
        <div data-testid="health-strip-scope"
             style={{ fontSize: 11, color: '#6C757D', marginBottom: 6 }}>
          {scopeLabel}
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 12 }}>
      {showHealth && metrics && (
        <>
          {/* 1 — every OPEN opportunity, and open-only is correct here.
                 ⚠⚠ TILES 3 AND 4 ARE NO LONGER SUBSETS OF THIS NUMBER. They
                 measure WORK STATE and include won deals, which have left the
                 open pipeline. Six tiles on one row invite a reader to assume
                 they partition the first one; they do not, and the break is
                 stated on tiles 3 and 4 ("N carry a recorded win" / "N won")
                 rather than by qualifying this label — the containment claim was
                 never made here, and weakening tile 1 to defend it would blur
                 the one tile that IS a clean total. */}
          <KPI label="Open opportunities" value={metrics.openOpportunities ?? 0} color="#0EA5E9" icon={Activity} />

          {/* 2 — unweighted, coverage inline, via the SHARED renderer. */}
          <KPI label="Open pipeline value" color="#15803D" icon={DollarSign} compact
            value={<MoneyWithCoverage sum={metrics.openValue?.sum} withValue={metrics.openValue?.withValue} count={metrics.openValue?.count} />} />

          {/* 3 — WORK STATE, not deal status: opportunities on stages that mean
                 the therapy happened, whether the deal is open or won.
                 ⚠ THE OLD NOTE HERE SAID "not a recorded win" FLATLY, and once
                 won deals are included that is false for the rows that DO carry
                 one — measured 2026-09-07: 13 of 154. The note is now derived,
                 so it cannot drift from the number beside it.
                 ⚠ Membership is still INFERRED from stage names for the rest;
                 that caveat stays, and stays ON the tile, never in a tooltip. */}
          <KPI label="Therapy delivered" color="#7C3AED" icon={CheckCircle2} compact
            value={<span>{metrics.delivered?.count ?? 0} · <MoneyWithCoverage
              sum={metrics.delivered?.value} withValue={metrics.delivered?.withValue} count={metrics.delivered?.count} /></span>}
            note={`⚠ stage-derived · ${metrics.delivered?.wonCount ?? 0} carry a recorded win`} />

          {/* 4 — WORK STATE: money committed, therapy not delivered.
                 ⚠⚠ THE LABEL DOES NOT OVERREACH, and the sub-line is why.
                 Ruling 2 widened this tile to absorb won deals whose STAGE
                 asserts nothing about the work (measured: Estimate / Invoice,
                 3 · $8,550) — real won money that previously appeared on no
                 tile at all. That makes three DIFFERENT kinds of evidence sit
                 under one heading, so they are counted separately and named:
                   paid       money we have a record of receiving
                   attended   someone showed up; nothing paid
                   won only   the deal is won and that is ALL we know
                 ⚠ `won only` is deliberately NOT folded into `paid` — these
                 rows are not known to be paid, and merging them would assert
                 money received that we have no record of. That is the same
                 error as an unqualified sum one tile over. */}
          <KPI label="Awaiting delivery" color="#0891B2" icon={Clock} compact
            value={<span>{metrics.awaiting?.count ?? 0} · <MoneyWithCoverage
              sum={metrics.awaiting?.value} withValue={metrics.awaiting?.withValue} count={metrics.awaiting?.count} /></span>}
            note={`${metrics.awaiting?.paid ?? 0} paid · ${metrics.awaiting?.attended ?? 0} attended · ${metrics.awaiting?.wonOnly ?? 0} won only · ${metrics.awaiting?.wonCount ?? 0} carry a recorded win`} />

          {/* 5 — the ledger problem made visible instead of buried in a
                 coefficient. Denominator is the BOARD's (resolvedTotal), so the
                 strip and the board cannot disagree about the pipeline's size.
                 ⚠⚠ IT IS NOW ANSWERABLE. 3,183 as a single reasonless number was
                 the most valuable gap on the strip, and the stage taxonomy
                 already held the answer — so the tile carries a VISIBLE control
                 into the breakdown. Deliberately a bordered, filled, labelled
                 button and not a chevron or an icon: an icon-only affordance on
                 a dense strip is one nobody finds, and jsdom never applies
                 App.css so a passing render test would prove nothing about
                 whether it can be seen. The style is inline for that reason. */}
          <KPI label="Dead / disqualified" color="#DC2626" icon={AlertTriangle} compact
            value={`${metrics.dead?.count ?? 0} of ${metrics.resolvedTotal ?? 0} · ${pct(metrics.dead?.count, metrics.resolvedTotal)}`}
            note={metrics.classifiedByFlag === 0 ? '⚠ stage-derived, no stage flagged' : undefined}
            action={(metrics.dead?.byStage || []).length > 0 ? (
              <button
                type="button"
                data-reasons-toggle
                aria-expanded={reasonsOpen}
                onClick={() => setReasonsOpen((v) => !v)}
                style={{
                  marginTop: 6, width: '100%', padding: '4px 8px',
                  border: '1px solid #DC2626', borderRadius: 6,
                  background: reasonsOpen ? '#DC2626' : '#FFFFFF',
                  color: reasonsOpen ? '#FFFFFF' : '#DC2626',
                  fontSize: 10, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}>
                {(metrics.dead.byStage || []).length} reasons
                <ChevronRight size={10} style={{ transform: reasonsOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
              </button>
            ) : undefined} />

          {/* 6 — the only CONTACT-scoped tile in an opportunity strip, and the
                 label says so. 'converted' is excluded: without it, 384
                 converted patients were reported as going cold. */}
          <KPI label="No contact 30d+ (contacts)" color="#F59E0B" icon={Calendar} compact
            value={`${metrics.contacts?.stale30d ?? 0} of ${metrics.contacts?.eligible ?? 0}`} />
        </>
      )}
      </div>
      {/* Full width, BELOW the strip: seven rows with four columns each cannot
          live inside a grid cell, and a summary row should stay scannable. */}
      {reasonsOpen && metrics && (
        <DeadReasonPanel dead={metrics.dead} resolvedTotal={metrics.resolvedTotal} />
      )}
    </>
  );
}

function KPI({ label, value, color, icon: Icon, compact, note, action }) {
  return (
    <div style={{ ...cardStyle, padding: 10, borderColor: color + '33', background: color + '08' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
        <Icon size={11} color={color} />
        <span style={{ fontSize: 9, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: compact ? 13 : 18, fontWeight: 800, color }}>{value}</div>
      {/* ⚠ Qualification sits ON the tile, never in a tooltip — a caveat a
          reader has to hover for is a caveat that does not exist. */}
      {note && (
        <div data-kpi-note style={{ fontSize: 9, color: '#6C757D', marginTop: 2, lineHeight: 1.3 }}>{note}</div>
      )}
      {action}
    </div>
  );
}

/**
 * ⚠⚠ WHY THE REASONS ARE NOT GROUPED.
 * `Bad Leads`, `Bad Leads / No Qualify`, `Doesn't Qualified` and
 * `Disqualified - Not a Fit` read as four names for one thing. WHICH of them
 * collapse together is CARLOS'S taxonomy ruling, not a decision to be taken
 * inside a renderer — so each keeps its own row and its own count, and the
 * near-synonymy is stated where he will see it.
 *
 * ⚠ The four names in the note are a MEASURED 2026-09-07 observation, not a
 * live derivation. If GHL renames a stage the note goes stale while the rows
 * stay correct; naming them is reporting, deriving "which are synonyms" would
 * be ruling, and the staleness is the cheaper of the two failures.
 */
function DeadReasonPanel({ dead, resolvedTotal }) {
  const rows = (dead && dead.byStage) || [];
  if (rows.length === 0) return null;
  const total = dead.count || 0;
  return (
    <div data-dead-reason-panel style={{ ...cardStyle, marginBottom: 12, padding: 12, borderColor: '#DC262633', background: '#FEF2F2' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#DC2626', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Why they are dead
        </span>
        {/* Every count carries its denominator, here and in every row. */}
        <span style={{ fontSize: 10, color: '#6C757D' }}>
          {total} of {resolvedTotal || 0} opportunities · {rows.length} distinct stages
        </span>
      </div>
      <div className="responsive-scroll">
        <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6C757D', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              <th style={{ padding: '3px 6px' }}>Stage (as GHL holds it)</th>
              <th style={{ padding: '3px 6px', textAlign: 'right' }}>Count</th>
              <th style={{ padding: '3px 6px', textAlign: 'right' }}>Share of dead</th>
              <th style={{ padding: '3px 6px', textAlign: 'right' }}>Share of pipeline</th>
              <th style={{ padding: '3px 6px' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.stage} data-reason-row style={{ borderTop: '1px solid #FECACA' }}>
                <td style={{ padding: '4px 6px', color: '#0F172A' }}>
                  {r.stage}
                  {/* A guess is labelled as one, per row. */}
                  {r.basis === 'name_fallback' && (
                    <span title="inferred from the stage name — no is_loss flag" style={{ color: '#B45309', marginLeft: 4 }}>⚠</span>
                  )}
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#0F172A' }}>{r.count}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#6C757D' }}>{pct(r.count, total)}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: '#6C757D' }}>{pct(r.count, resolvedTotal)}</td>
                <td style={{ padding: '4px 6px' }}>
                  {/* The SHARED renderer — coverage 0 prints nothing at all. */}
                  <MoneyWithCoverage sum={r.value} withValue={r.withValue} count={r.count} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div data-reason-caveats style={{ fontSize: 9, color: '#6C757D', marginTop: 8, lineHeight: 1.45 }}>
        ⚠ Reasons are GHL stage names, <strong>ungrouped</strong>. Measured 7 Sep,
        four read as near-synonyms — <em>Bad Leads</em>, <em>Bad Leads / No Qualify</em>,
        {' '}<em>Doesn&apos;t Qualified</em> and <em>Disqualified - Not a Fit</em>.
        Whether they are one category is a taxonomy ruling, so they are shown
        separately rather than merged here.
        {(dead.byStage || []).every((r) => r.basis === 'name_fallback') && (
          <> ⚠ No stage carries an <code>is_loss</code> flag, so every reason above is
          inferred from its name. Ticking those flags in GHL replaces the guess with a fact.</>
        )}
      </div>
    </div>
  );
}

/* ─── G.3 — Stage Advancement Suggestions banner ──────────────────────── */

export function AdvanceSuggestionsBanner({ role, onContactClick }) {
  const [suggestions, setSuggestions] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const allowed = role && canUse(role, 'pipeline_advance_suggestions');
  if (!allowed) return null;

  async function fetchSuggestions() {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.aiPipelineAdvanceSuggestions();
      if (r && r.success) {
        setSuggestions(r.suggestions || []);
      }
    } finally { setLoading(false); setLoaded(true); }
  }

  if (!loaded && !loading) {
    return (
      <div style={{ ...cardStyle, marginBottom: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Sparkles size={14} color="#A855F7" />
        <span style={{ fontSize: 12, color: '#475569', flex: 1 }}>
          AI can identify which contacts are ready to advance to the next stage.
        </span>
        <button onClick={fetchSuggestions} style={{
          padding: '5px 10px', borderRadius: 6, border: 'none',
          background: 'linear-gradient(135deg, #A855F7 0%, #2E8B98 100%)',
          color: '#FFF', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <Sparkles size={11} /> Run AI scan
        </button>
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ ...cardStyle, marginBottom: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#94A3B8' }}>
        <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
        Scanning pipeline for advancement candidates…
      </div>
    );
  }
  if (suggestions.length === 0) {
    return (
      <div style={{ ...cardStyle, marginBottom: 12, padding: '10px 12px', fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={12} color="#A855F7" />
        AI didn&apos;t find any contacts with strong advancement signals right now.
        <button onClick={fetchSuggestions} style={{ marginLeft: 'auto', padding: '3px 8px', border: '1px solid #CBD5E1', background: '#FFF', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
          <RefreshCw size={10} /> Re-scan
        </button>
      </div>
    );
  }
  return (
    <div style={{ ...cardStyle, marginBottom: 12, borderColor: '#A855F7', background: '#FAF5FF' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}>
        <Sparkles size={14} color="#A855F7" />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED' }}>
          {suggestions.length} contact{suggestions.length !== 1 ? 's' : ''} ready to advance
        </span>
        <span style={{ flex: 1, fontSize: 11, color: '#94A3B8' }}>
          {expanded ? 'click to collapse' : 'click to review'}
        </span>
        <ChevronRight size={14} color="#A855F7" style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      </div>
      {expanded && (
        <div style={{ padding: '0 10px 10px' }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => onContactClick && onContactClick(s.contact_id)}
              style={{ padding: 8, borderTop: '1px solid #E9D5FF', cursor: 'pointer',
                       display: 'flex', alignItems: 'center', gap: 8 }}>
              <Target size={12} color={s.confidence === 'high' ? '#15803D' : s.confidence === 'medium' ? '#F59E0B' : '#94A3B8'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#0F172A' }}>
                  <strong>Contact #{s.contact_id}</strong> — {s.current_stage} → <strong>{s.suggested_stage}</strong>
                  <span style={{ fontSize: 9, padding: '1px 6px', marginLeft: 6, borderRadius: 4,
                                background: s.confidence === 'high' ? '#DCFCE7' : '#FEF3C7',
                                color: s.confidence === 'high' ? '#15803D' : '#B45309',
                                textTransform: 'uppercase', fontWeight: 700 }}>
                    {s.confidence}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{s.rationale}</div>
              </div>
              <ChevronRight size={12} color="#94A3B8" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── G.6 — Pipeline Copilot (NL query) ────────────────────────────────── */

export function CopilotChat({ role }) {
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const allowed = role && canUse(role, 'pipeline_copilot');
  if (!allowed) return null;

  async function ask() {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.aiPipelineCopilotQuery(q);
      if (r && r.success) {
        setHistory(prev => [{ q, answer: r }, ...prev].slice(0, 5));
        setQuery('');
      } else {
        setHistory(prev => [{ q, error: r?.error || 'failed' }, ...prev].slice(0, 5));
      }
    } finally { setLoading(false); }
  }

  return (
    <div style={{ ...cardStyle, marginBottom: 12, borderColor: '#0EA5E9' }}>
      <div onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, cursor: 'pointer' }}>
        <Brain size={14} color="#0EA5E9" />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0369A1' }}>Pipeline Copilot</span>
        <span style={{ fontSize: 11, color: '#94A3B8', flex: 1 }}>
          {open ? 'ask a natural-language question' : 'click to ask a question'}
        </span>
        <ChevronRight size={14} color="#0EA5E9" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      </div>
      {open && (
        <div style={{ padding: '0 10px 10px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && ask()}
              placeholder='e.g. "show me deals at risk this quarter"'
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 6,
                border: '1px solid #CBD5E1', fontSize: 12,
              }} />
            <button onClick={ask} disabled={loading || !query.trim()} style={{
              padding: '6px 12px', borderRadius: 6, border: 'none',
              background: '#0EA5E9', color: '#FFF', fontSize: 11, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {loading ? <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={11} />}
              Ask
            </button>
          </div>
          {history.map((h, i) => (
            <div key={i} style={{ padding: 8, borderTop: '1px solid #E2E8F0', fontSize: 11 }}>
              <div style={{ color: '#0369A1', fontWeight: 600, marginBottom: 4 }}>Q: {h.q}</div>
              {h.error ? (
                <div style={{ color: '#DC2626' }}>✗ {h.error}</div>
              ) : (
                <>
                  <div style={{ color: '#0F172A', marginBottom: 3 }}>{h.answer.interpretation}</div>
                  <div style={{ color: '#475569', marginBottom: 3, fontStyle: 'italic' }}>Filter: {h.answer.filterSummary}</div>
                  {h.answer.explanation && <div style={{ color: '#475569' }}>{h.answer.explanation}</div>}
                  {Array.isArray(h.answer.caveats) && h.answer.caveats.length > 0 && (
                    <ul style={{ margin: '4px 0 0', paddingLeft: 14, color: '#94A3B8', fontSize: 10 }}>
                      {h.answer.caveats.map((c, j) => <li key={j}>{c}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── G.2 — Deal Health Score chip (per pipeline card) ─────────────────── */

export function DealHealthChip({ contactId, role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const allowed = role && canView(role, 'deal_health_score');
  const canRun = role && canUse(role, 'deal_health_score');
  if (!allowed) return null;

  async function fetch() {
    if (!canRun) return;
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.aiDealHealthScore(contactId);
      if (r && r.success) setData(r);
    } finally { setLoading(false); }
  }

  if (!data) {
    return (
      <button onClick={(e) => { e.stopPropagation(); fetch(); }} disabled={!canRun || loading}
        title={canRun ? 'AI Deal Health' : 'view-only'}
        style={{
          padding: '2px 6px', borderRadius: 10, border: '1px dashed #94A3B8',
          background: '#FFF', fontSize: 9, fontWeight: 700, color: '#64748B',
          cursor: canRun ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 3,
        }}>
        {loading ? <RefreshCw size={9} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={9} />}
        Deal Health
      </button>
    );
  }
  const color = data.score >= 80 ? '#15803D' : data.score >= 50 ? '#F59E0B' : '#DC2626';
  return (
    <span onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      title={data.rationale}
      style={{
        padding: '2px 6px', borderRadius: 10,
        background: color + '20', color, fontSize: 9, fontWeight: 700,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
      Health {data.score}/100
    </span>
  );
}

/* ─── G.5 — Next Best Action chip (per pipeline card) ──────────────────── */

export function NextBestActionChip({ contactId, role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const allowed = role && canUse(role, 'next_best_action');
  if (!allowed) return null;

  async function fetch() {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.aiNextBestAction(contactId);
      if (r && r.success) setData(r);
    } finally { setLoading(false); }
  }

  if (!data) {
    return (
      <button onClick={(e) => { e.stopPropagation(); fetch(); }} disabled={loading}
        title="AI Next Best Action"
        style={{
          padding: '2px 6px', borderRadius: 10, border: '1px dashed #2E8B98',
          background: '#FFF', fontSize: 9, fontWeight: 700, color: '#0F766E',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
        }}>
        {loading ? <RefreshCw size={9} style={{ animation: 'spin 1s linear infinite' }} /> : <Target size={9} />}
        NBA
      </button>
    );
  }
  const urgencyColor = data.urgency === 'high' ? '#DC2626' : data.urgency === 'medium' ? '#F59E0B' : '#10B981';
  return (
    <span title={data.rationale}
      style={{
        padding: '2px 6px', borderRadius: 10,
        background: urgencyColor + '20', color: urgencyColor,
        fontSize: 9, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
      <Target size={9} /> {data.action}
    </span>
  );
}
